import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Link, Routes, Route, useLocation } from 'react-router-dom';
import PendingOrdersForm from '../components/forms/PendingOrdersForm';
import axiosInstance from '../api/axiosInstance';
import { EcommerceService } from '../services/ecommerceService';
import { STORE_STYLES } from '../assets/data/arrays';

const CREAR_ENVIO_STORES = 'Bigcenter,Distrito Cafetero,Artiq,Garantias,Baristico';

const getStoreBadge = (storeName?: string | null) => {
  const name = (storeName ?? '').trim();
  const row = STORE_STYLES.find(
    (s) => s.value === name || (name === 'Barístico' && s.value === 'Baristico')
  );
  if (row) {
    return { prefix: row.shortValue, className: row.color };
  }
  return { prefix: '', className: '' };
};

interface Order {id: number;orders_id: string;customer_full_name: string;date_created: string;store_name: string;
status: string;total: string;payment_method?: string;transaction_id?: string;
  extraDetails?: { payment_method?: string; transaction_id?: string;};
}
interface OrderDetail {
  id: number;
  orders_id: number;
  origin_order_id: number;
  store_name: string;
  status: string;
  total: string;
  guia?: string;
  date_created: string;
  date_modified: string;
  agente?: number;
  customer: { first_name: string; last_name: string; email: string; document?: string; tipo_documento?: string; tipo_persona?: string;};
  billingAddress: { address_1: string; city: string; state: string; country: string; postcode: string; phone?: string;
  };
  shippingAddress: { address_1: string; city: string; state: string; country: string; postcode: string; phone?: string;
  };
  lineItems: Array<{ product_id: string; product_name: string; quantity: number; price: string;}>;
}

const PendingOrders: React.FC = () => {
  const location = useLocation();
  const [orders, setOrders] = useState<Order[]>([]);
  const [selectedOrder, setSelectedOrder] = useState<OrderDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingOrderDetails, setIsLoadingOrderDetails] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [ordersWithoutRemision, setOrdersWithoutRemision] = useState<Set<number>>(new Set())
  const observer = useRef<IntersectionObserver | null>(null);
  const lastOrderElementRef = useCallback((node: HTMLDivElement) => {
    if (isLoading) return;
    if (observer.current) observer.current.disconnect();
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore && !isLoadingMore) {
        setPage(prevPage => prevPage + 1);
      }
    });
    if (node) observer.current.observe(node);
  }, [isLoading, hasMore, isLoadingMore]);

  const fetchProcessingOrders = async (pageNumber: number) => {
    try {
      setIsLoadingMore(true);
      const token = localStorage.getItem('accessToken');
      const response = await axiosInstance.get(`/list?&limit=20&page=${pageNumber}&states=processing&stores=${CREAR_ENVIO_STORES}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      // Filtrar pedidos con payment_method="wompi" que no tienen transaction_id
      const filteredOrders = response.data.data.filter((order: Order) => {
        const paymentMethod = order.payment_method || order.extraDetails?.payment_method;
        const transactionId = order.transaction_id || order.extraDetails?.transaction_id;
        if (paymentMethod === 'wompi') {return transactionId && transactionId.trim() !== '';}
        return true;
      });

      const formattedOrders = filteredOrders.map((order: Order) => ({
        ...order, date_created: new Date(order.date_created).toLocaleString(),
      }));

      if (pageNumber === 1) {
        setOrders(formattedOrders)
      } else {
        setOrders(prevOrders => [...prevOrders, ...formattedOrders])
      }

      // Verificar remisiones para los pedidos cargados en esta página
      const pageIds = formattedOrders.map((o: Order) => o.id)
      try {
        const missingSet = await EcommerceService.getOrdersWithoutRemision(pageIds)
        const missingAsNumbers = new Set<number>(Array.from(missingSet).map(id => Number(id)))
        setOrdersWithoutRemision(prev => {
          if (pageNumber === 1) return missingAsNumbers
          const merged = new Set<number>(prev)
          missingAsNumbers.forEach(v => merged.add(v))
          return merged
        })
      } catch (err) {
        console.error('Error verificando remisiones OFIMA:', err)
      }

      setHasMore(formattedOrders.length === 20);
    } catch (error) {
      console.error('Error fetching processing orders:', error);
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  };

  useEffect(() => {fetchProcessingOrders(page);}, [page]);

  const handleOrderClick = async (orderId: number) => {
    try {
      setIsLoadingOrderDetails(true);
      const response = await axiosInstance.get(`/list/${orderId}`);
      setSelectedOrder(response.data);
    } catch (error) {
      console.error('Error fetching order details:', error);
    } finally {
      setIsLoadingOrderDetails(false);
    }
  };

  return (
    <div className="flex h-full">
      <div className="w-48 bg-white border-r border-gray-200 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 64px)' }}>
        <div className="p-2">
          <h4 className="text-md font-semibold text-gray-800 mb-4">Pedidos Pendientes</h4>
          {isLoading && page === 1 ? (
            <div className="text-center text-gray-500">Cargando pedidos...</div>
          ) : (
            <div className="space-y-2">
              {orders.map((order, index) => {
                const storeBadge = getStoreBadge(order.store_name);
                return (
                <div key={order.id} ref={index === orders.length - 1 ? lastOrderElementRef : null}>
                  <Link to={`/envios/pending/${order.id}`}
                    aria-disabled={ordersWithoutRemision.has(order.id)}
                    onClick={(e) => {
                      if (ordersWithoutRemision.has(order.id)) {
                        e.preventDefault()
                        return
                      }
                      handleOrderClick(order.id)
                    }}
                    title={ordersWithoutRemision.has(order.id) ? 'Este pedido aún no tiene remisión' : undefined}
                    aria-label={ordersWithoutRemision.has(order.id) ? 'Pedido sin remisión: no se puede cotizar' : `Abrir pedido ${order.id}`}
                    className={`text-sm block p-3 rounded-lg transition-colors ${
                      ordersWithoutRemision.has(order.id)
                        ? 'bg-red-50 text-red-600 cursor-not-allowed'
                        : location.pathname === `/envios/pending/${order.id}`
                          ? 'bg-blue-50 text-custom-blue'
                          : 'hover:bg-gray-50 text-gray-700'}`}>
                    <div className="font-semibold">
                      {storeBadge.prefix ? (
                        <span className={storeBadge.className}> {storeBadge.prefix}</span>
                      ) : null}
                      {' '}#{order.id}
                    </div>
                    <div className="text-xs text-gray-500">{order.customer_full_name}</div>
                    <div className={`text-xs ${ordersWithoutRemision.has(order.id) ? 'text-red-500' : 'text-gray-400'}`}>
                      <span>
                        {order.date_created}
                      </span>
                    </div>
                  </Link>
                </div>
              )})}
              {isLoadingMore && (
                <div className="text-center text-gray-500 py-2">Cargando más pedidos...</div>
              )}
              {!isLoading && orders.length === 0 && (
                <div className="text-center text-gray-500">No hay pedidos en proceso</div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Contenido principal */}
      <div className="flex-1 overflow-y-auto p-6" style={{ maxHeight: 'calc(100vh - 64px)' }}>
        {isLoadingOrderDetails ? (
          <div className="flex flex-col items-center justify-center h-full">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-800 mb-4"></div>
            <p className="text-gray-600 text-lg font-medium">Cargando detalles del pedido...</p>
            <p className="text-gray-500 text-sm mt-2">Obteniendo información completa del pedido</p>
          </div>
        ) : (
          <Routes>
            <Route path="/" element={<PendingOrdersForm/>} /> 
            <Route path="/:id" element={<PendingOrdersForm orderDetail={selectedOrder} />} />
          </Routes>
        )}
      </div>
    </div>
  );
};

export default PendingOrders; 